import { type FC, useState } from 'react'
import { useHref, useNavigate } from 'react-router-dom'
import { Button } from '../../../../../components/Button/Button'
import { Modal } from '../../../../../components/Modal/Modal'
import { useZocksAuth } from '../../../../../components/ScopedBlock/hooks'
import { Spinner } from '../../../../../components/Spinner/Spinner'
import { useAccount } from '../../../../../common/useAccount'
import { type TemplateValue } from '../../../../../models/TemplateLibrary'
import { useListDetailedTemplatesQuery } from '../../../../../store/templateLibrary/templateLibrary'
import { type SchedulingEmail } from '../../../Templates/SchedulingEmails/schedulingEmails'
import { SCHEDULING_EMAIL_TEMPLATE_TYPE } from '../../../Templates/TemplatesTab'
import { useTemplateCreators } from '../../../Templates/useTemplateCreators'
import { currentFirst } from './currentFirst'
import { TemplatePicker } from './TemplatePicker'

export type ChooseTemplateMode = 'choose' | 'view'

interface ChooseSchedulingEmailTemplateModalProps {
  email: SchedulingEmail
  currentTemplateId?: string
  mode: ChooseTemplateMode
  subtitle?: string
  openTemplateInNewTab?: boolean
  onSelect: (template: TemplateValue) => Promise<void>
  onHide: () => void
}

const TEMPLATE_EDITOR_PATH = '/workspace/setup/templates/template'

export const ChooseSchedulingEmailTemplateModal: FC<ChooseSchedulingEmailTemplateModalProps> = ({
  email,
  currentTemplateId,
  mode,
  subtitle,
  openTemplateInNewTab = false,
  onSelect,
  onHide,
}) => {
  const navigate = useNavigate()
  const templateEditorHref = useHref(TEMPLATE_EDITOR_PATH)
  const { accountId, account } = useAccount()
  const { hasScope } = useZocksAuth()
  const { data, isLoading, isError } = useListDetailedTemplatesQuery(
    { accountId, type: SCHEDULING_EMAIL_TEMPLATE_TYPE, typeId: email.slug, defaultTypeId: email.slug, scopes: ['system', 'account', 'enterprise'] },
    { refetchOnMountOrArgChange: true },
  )
  const usersById = useTemplateCreators(data?.data)
  const [selectedId, setSelectedId] = useState(currentTemplateId)
  const [isSelecting, setIsSelecting] = useState(false)

  const templates = currentFirst(data?.data ?? [], currentTemplateId)
  const selected = templates.find((template) => template.id === selectedId) ?? templates[0]
  const canEditTemplates = hasScope('**', 'tmpl_lib:admin', ['tmpl_lib:admin']) && !(account?.enterpriseConfig.lockTemplates ?? true)
  const isCurrentSelected = selected != null && selected.id === currentTemplateId

  const openTemplate = (templateId: string) => {
    if (openTemplateInNewTab) {
      window.open(`${templateEditorHref}/${templateId}`, '_blank', 'noopener')
    } else {
      navigate(`${TEMPLATE_EDITOR_PATH}/${templateId}`)
    }
  }

  const handleSelect = async () => {
    if (!selected) {
      return
    }
    setIsSelecting(true)
    try {
      await onSelect(selected)
      onHide()
    } catch {
      setIsSelecting(false)
    }
  }

  return (
    <Modal
      show
      size='xl'
      fullscreen='lg-down'
      scrollable
      backdrop={isSelecting ? 'static' : true}
      keyboard={!isSelecting}
      onHide={isSelecting ? () => undefined : onHide}
      header={
        <div className='d-flex flex-column'>
          <h4 className='mb-0'>{mode === 'choose' ? `Choose ${email.label} template` : `${email.label} templates`}</h4>
          <span className='paragraph-medium zocks-text-gray-600'>{subtitle ?? `For all ${email.label} emails`}</span>
        </div>
      }
      body={
        isLoading ? (
          <div className='d-flex justify-content-center py-5'>
            <Spinner />
          </div>
        ) : isError || !selected ? (
          <div className='paragraph-small zocks-text-gray-600'>Could not load the templates. Please try again.</div>
        ) : (
          <TemplatePicker
            templates={templates}
            selected={selected}
            currentTemplateId={currentTemplateId}
            usersById={usersById}
            onSelect={setSelectedId}
            actions={
              mode === 'choose' ? (
                <div className='d-flex gap-2 flex-shrink-0'>
                  <Button type='button' variant='secondaryGhostButton' disabled={isSelecting} onClick={() => openTemplate(selected.id)}>
                    {canEditTemplates && selected.scope === 'account' ? 'Edit template' : 'View template'}
                  </Button>
                  {isCurrentSelected ? (
                    <Button type='button' variant='primary' disabled className='d-flex align-items-center gap-1'>
                      <i className='bi bi-check' aria-hidden='true' />
                      Selected
                    </Button>
                  ) : (
                    <Button type='button' variant='primary' loading={isSelecting} onClick={handleSelect}>
                      {isSelecting ? 'Selecting' : 'Select template'}
                    </Button>
                  )}
                </div>
              ) : null
            }
          />
        )
      }
    />
  )
}
